import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { toast } from 'react-toastify'

import { EzosLoader } from '../../../../components'
import { useAppDispatch, useUser } from '../../../../hooks'
import { setWorkSpaceUser } from '../../../../store'
import {
    CommunityFurnished,
    CommunityLeaseStyle,
    CommunityProfile,
} from '../../../../models/Company/types'
import {
    emptyCommunityProfile,
    isCommunityProfileDirty,
    useCommunityProfile,
} from '../../../../hooks/useCommunityProfile'
import {
    useDeleteApartmentLogo,
    useUploadApartmentLogo,
} from '../../../../hooks/useMoveOutManagement/useMoveOutManagement'
import { CommunityFactRow } from './CommunityFactRow'
import { CommunityHoursSection } from './CommunityHoursSection'
import { FillFromWebsiteDialog } from './FillFromWebsiteDialog'
import { formatLastFilledAt } from './fillFromWebsiteReview'

const ANIMAL_TYPES = [
    { id: 'dog', label: 'Dog' },
    { id: 'cat', label: 'Cat' },
    { id: 'fish', label: 'Fish' },
    { id: 'reptile', label: 'Reptile' },
    { id: 'rodent', label: 'Rodent' },
    { id: 'bird', label: 'Bird' },
]

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        maxWidth: 920,
        paddingBottom: theme.spacing(4),
    },
    rootEmbedded: {
        maxWidth: 'none',
        paddingBottom: 0,
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    caption: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        lineHeight: 1.45,
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: '0.95rem',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: theme.spacing(1.25),
    },
    actions: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    logoRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    logoPreview: {
        height: 56,
        maxWidth: 180,
        objectFit: 'contain',
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        padding: theme.spacing(0.5),
    },
    fillButton: {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&.Mui-disabled': {
            color: '#fff',
            opacity: 0.65,
        },
    },
    faqCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1.25),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
}))

type Props = {
    apartmentId: number
    embedded?: boolean
    onWebsiteUrlSaved?: (url: string) => void
    onProfilePictureSaved?: (url: string) => void
}

export const CommunitySettings: React.FC<Props> = ({
    apartmentId,
    embedded,
    onWebsiteUrlSaved,
    onProfilePictureSaved,
}) => {
    const classes = useStyles()
    const dispatch = useAppDispatch()
    const { workspaceUser } = useUser()
    const { profile, isLoading, isSaving, saveProfile } = useCommunityProfile(
        apartmentId,
    )
    const [draft, setDraft] = useState<CommunityProfile>(
        emptyCommunityProfile(),
    )
    const [fillOpen, setFillOpen] = useState(false)

    useEffect(() => {
        if (profile) setDraft(profile)
    }, [profile])

    const setField = <K extends keyof CommunityProfile>(
        key: K,
        value: CommunityProfile[K],
    ) => {
        setDraft((prev) => ({ ...prev, [key]: value }))
    }

    const syncWorkspace = (patch: {
        url?: string
        profile_picture?: string
    }) => {
        if (workspaceUser?.active_workspace) {
            const active = { ...workspaceUser.active_workspace }
            if (patch.url != null) active.url = patch.url
            if (patch.profile_picture !== undefined)
                active.profile_picture = patch.profile_picture
            dispatch(
                setWorkSpaceUser({
                    ...workspaceUser,
                    active_workspace: active,
                }),
            )
        }
        if (patch.url != null) onWebsiteUrlSaved?.(patch.url)
        if (patch.profile_picture !== undefined)
            onProfilePictureSaved?.(patch.profile_picture)
    }

    const handleLogoSaved = (url: string) => {
        setDraft((prev) => ({ ...prev, profile_picture: url }))
        syncWorkspace({ profile_picture: url })
    }

    const handleSave = async () => {
        const saved = await saveProfile(draft)
        if (saved?.website_url != null) {
            syncWorkspace({ url: saved.website_url })
        }
    }

    const handleExtract = () => {
        setFillOpen(true)
    }

    const isDirty = useMemo(() => isCommunityProfileDirty(draft, profile), [
        draft,
        profile,
    ])

    if (isLoading && !profile) {
        return <EzosLoader />
    }

    const lastFilledAt = profile?.website_filled_at || null

    return (
        <Box
            className={
                embedded
                    ? `${classes.root} ${classes.rootEmbedded}`
                    : classes.root
            }
        >
            <Box className={classes.header}>
                <Typography className={classes.caption}>
                    Property facts the voice agent and leasing setup reuse. Fill
                    from website reads the property site and lets you choose
                    what to keep when something is already saved.
                </Typography>
                <Box className={classes.actions}>
                    <Button
                        variant="contained"
                        className={classes.fillButton}
                        onClick={handleExtract}
                        disabled={isSaving}
                    >
                        Fill from website
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleSave}
                        disabled={!isDirty || isSaving}
                    >
                        {isSaving ? 'Saving…' : 'Save'}
                    </Button>
                </Box>
            </Box>

            <TextField
                label="Website URL"
                placeholder="https://www.property.com"
                value={draft.website_url || ''}
                onChange={(e) => setField('website_url', e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                helperText="Fill from website reads this URL. Saving keeps it on the property."
            />
            <CommunityLogoRow
                apartmentId={apartmentId}
                currentUrl={
                    draft.profile_picture || profile?.profile_picture || ''
                }
                onSaved={handleLogoSaved}
            />
            {lastFilledAt && (
                <Typography className={classes.caption}>
                    Last filled from website {formatLastFilledAt(lastFilledAt)}
                    {profile?.website_fill_url
                        ? ` · ${profile.website_fill_url}`
                        : ''}
                    .
                </Typography>
            )}

            <Box className={classes.section}>
                <CommunityHoursSection
                    value={draft.office_hours}
                    onChange={(hours) => setField('office_hours', hours)}
                />
            </Box>

            <Box className={classes.section}>
                <CommunityFactRow
                    label="Amenities"
                    value={draft.amenities}
                    onChange={(value) => setField('amenities', value)}
                />
                <CommunityFactRow
                    label="Parking"
                    value={draft.parking}
                    onChange={(value) => setField('parking', value)}
                />
                <CommunityFactRow
                    label="Laundry"
                    value={draft.laundry}
                    onChange={(value) => setField('laundry', value)}
                />
                <CommunityFactRow
                    label="Utilities included"
                    value={draft.utilities_included}
                    onChange={(value) => setField('utilities_included', value)}
                />
                <CommunityFactRow
                    label="Income requirements"
                    value={draft.income_requirements}
                    onChange={(value) => setField('income_requirements', value)}
                />
                <CommunityFactRow
                    label="Security deposit"
                    value={draft.security_deposit}
                    onChange={(value) => setField('security_deposit', value)}
                />
                <CommunityFactRow
                    label="Insurance"
                    value={draft.insurance_notes}
                    onChange={(value) => setField('insurance_notes', value)}
                />
            </Box>

            <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                    Short answers
                </Typography>
                <Box className={classes.grid}>
                    <FormControl variant="outlined" size="small">
                        <InputLabel>Furnished</InputLabel>
                        <Select
                            label="Furnished"
                            value={draft.furnished}
                            onChange={(e) =>
                                setField(
                                    'furnished',
                                    e.target.value as CommunityFurnished,
                                )
                            }
                        >
                            <MenuItem value="unknown">Unknown</MenuItem>
                            <MenuItem value="yes">Furnished</MenuItem>
                            <MenuItem value="no">Unfurnished</MenuItem>
                            <MenuItem value="partial">Partial</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small">
                        <InputLabel>Lease style</InputLabel>
                        <Select
                            label="Lease style"
                            value={draft.lease_style}
                            onChange={(e) =>
                                setField(
                                    'lease_style',
                                    e.target.value as CommunityLeaseStyle,
                                )
                            }
                        >
                            <MenuItem value="unknown">Unknown</MenuItem>
                            <MenuItem value="individual">
                                Individual / by the bedroom
                            </MenuItem>
                            <MenuItem value="joint">Joint lease</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small">
                        <InputLabel>Roommate matching</InputLabel>
                        <Select
                            label="Roommate matching"
                            value={triStateValue(draft.roommate_matching)}
                            onChange={(e) =>
                                setField(
                                    'roommate_matching',
                                    parseTriState(e.target.value as string),
                                )
                            }
                        >
                            <MenuItem value="unknown">Unknown</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small">
                        <InputLabel>Students required</InputLabel>
                        <Select
                            label="Students required"
                            value={triStateValue(draft.students_required)}
                            onChange={(e) =>
                                setField(
                                    'students_required',
                                    parseTriState(e.target.value as string),
                                )
                            }
                        >
                            <MenuItem value="unknown">Unknown</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                    Pet defaults
                </Typography>
                <Typography className={classes.caption}>
                    Allowed animals, max pets, and notes the website usually
                    publishes. Copy these into Property Data when you set up a
                    leasing season.
                </Typography>
                <Box>
                    {ANIMAL_TYPES.map((animal) => (
                        <FormControlLabel
                            key={animal.id}
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={draft.pet_defaults.allowed_animal_types.includes(
                                        animal.id,
                                    )}
                                    onChange={(e) => {
                                        const selected = new Set(
                                            draft.pet_defaults.allowed_animal_types,
                                        )
                                        if (e.target.checked) {
                                            selected.add(animal.id)
                                        } else {
                                            selected.delete(animal.id)
                                        }
                                        setField('pet_defaults', {
                                            ...draft.pet_defaults,
                                            allowed_animal_types: Array.from(
                                                selected,
                                            ),
                                        })
                                    }}
                                />
                            }
                            label={animal.label}
                        />
                    ))}
                </Box>
                <TextField
                    label="Max pets per unit"
                    value={draft.pet_defaults.max_pets_per_unit}
                    onChange={(e) =>
                        setField('pet_defaults', {
                            ...draft.pet_defaults,
                            max_pets_per_unit: e.target.value,
                        })
                    }
                    variant="outlined"
                    size="small"
                />
                <CommunityFactRow
                    label="Pet notes"
                    value={draft.pet_notes}
                    onChange={(value) => setField('pet_notes', value)}
                />
            </Box>

            <Box className={classes.section}>
                <Box className={classes.header}>
                    <Typography className={classes.sectionTitle}>
                        Extra monthly fees
                    </Typography>
                    <Button
                        size="small"
                        onClick={() =>
                            setField('extra_fees', [
                                ...draft.extra_fees,
                                { name: '', amount: '', covers: '' },
                            ])
                        }
                    >
                        Add fee
                    </Button>
                </Box>
                {draft.extra_fees.map((fee, index) => (
                    <Box key={`fee-${index}`} className={classes.grid}>
                        <TextField
                            label="Name"
                            value={fee.name}
                            onChange={(e) =>
                                setField(
                                    'extra_fees',
                                    replaceAt(draft.extra_fees, index, {
                                        ...fee,
                                        name: e.target.value,
                                    }),
                                )
                            }
                            variant="outlined"
                            size="small"
                        />
                        <TextField
                            label="Amount"
                            value={fee.amount}
                            onChange={(e) =>
                                setField(
                                    'extra_fees',
                                    replaceAt(draft.extra_fees, index, {
                                        ...fee,
                                        amount: e.target.value,
                                    }),
                                )
                            }
                            variant="outlined"
                            size="small"
                        />
                        <TextField
                            label="What it covers"
                            value={fee.covers}
                            onChange={(e) =>
                                setField(
                                    'extra_fees',
                                    replaceAt(draft.extra_fees, index, {
                                        ...fee,
                                        covers: e.target.value,
                                    }),
                                )
                            }
                            variant="outlined"
                            size="small"
                        />
                        <Button
                            onClick={() =>
                                setField(
                                    'extra_fees',
                                    draft.extra_fees.filter(
                                        (_, i) => i !== index,
                                    ),
                                )
                            }
                        >
                            Remove
                        </Button>
                    </Box>
                ))}
            </Box>

            <Box className={classes.section}>
                <Box className={classes.header}>
                    <Box>
                        <Typography className={classes.sectionTitle}>
                            Frequently asked questions
                        </Typography>
                        <Typography className={classes.caption}>
                            Enabled answers are given to the voice agent. Turn
                            one off if the copy is stale.
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        onClick={() =>
                            setField('faqs', [
                                ...draft.faqs,
                                {
                                    question: '',
                                    answer: '',
                                    enabled: true,
                                    source_url: '',
                                },
                            ])
                        }
                    >
                        Add question
                    </Button>
                </Box>
                {draft.faqs.map((faq, index) => (
                    <Box key={`faq-${index}`} className={classes.faqCard}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={faq.enabled}
                                    onChange={(e) =>
                                        setField(
                                            'faqs',
                                            replaceAt(draft.faqs, index, {
                                                ...faq,
                                                enabled: e.target.checked,
                                            }),
                                        )
                                    }
                                />
                            }
                            label="Use on phone calls"
                        />
                        <TextField
                            label="Question"
                            value={faq.question}
                            onChange={(e) =>
                                setField(
                                    'faqs',
                                    replaceAt(draft.faqs, index, {
                                        ...faq,
                                        question: e.target.value,
                                    }),
                                )
                            }
                            variant="outlined"
                            size="small"
                        />
                        <TextField
                            label="Answer"
                            value={faq.answer}
                            onChange={(e) =>
                                setField(
                                    'faqs',
                                    replaceAt(draft.faqs, index, {
                                        ...faq,
                                        answer: e.target.value,
                                    }),
                                )
                            }
                            variant="outlined"
                            size="small"
                            multiline
                            rows={3}
                        />
                        <Box className={classes.actions}>
                            {faq.source_url && (
                                <Typography className={classes.caption}>
                                    {faq.source_url}
                                </Typography>
                            )}
                            <Button
                                onClick={() =>
                                    setField(
                                        'faqs',
                                        draft.faqs.filter(
                                            (_, i) => i !== index,
                                        ),
                                    )
                                }
                            >
                                Remove
                            </Button>
                        </Box>
                    </Box>
                ))}
            </Box>
            <FillFromWebsiteDialog
                open={fillOpen}
                apartmentId={apartmentId}
                currentProfile={draft}
                initialUrl={draft.website_url}
                onClose={() => setFillOpen(false)}
                onApplied={(saved) => {
                    setDraft(saved)
                    syncWorkspace({
                        url: saved.website_url,
                        profile_picture: saved.profile_picture,
                    })
                }}
            />
        </Box>
    )
}

function CommunityLogoRow({
    apartmentId,
    currentUrl,
    onSaved,
}: {
    apartmentId: number
    currentUrl?: string
    onSaved: (url: string) => void
}) {
    const classes = useStyles()
    const inputRef = useRef<HTMLInputElement>(null)
    const [overrideUrl, setOverrideUrl] = useState<string | null>(null)
    const {
        mutate: uploadLogo,
        isLoading: isUploading,
    } = useUploadApartmentLogo()
    const {
        mutate: deleteLogo,
        isLoading: isDeleting,
    } = useDeleteApartmentLogo()
    const busy = isUploading || isDeleting
    const current = (currentUrl || '').trim()
    const shown = overrideUrl !== null ? overrideUrl : current

    useEffect(() => {
        setOverrideUrl(null)
    }, [currentUrl])

    const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        const localPreview = URL.createObjectURL(file)
        setOverrideUrl(localPreview)
        uploadLogo(
            { apartmentId, file },
            {
                onSuccess: (data) => {
                    URL.revokeObjectURL(localPreview)
                    const url = data.profile_picture || ''
                    setOverrideUrl(url)
                    onSaved(url)
                    toast.success('Property logo updated')
                },
                onError: () => {
                    URL.revokeObjectURL(localPreview)
                    setOverrideUrl(null)
                    toast.error('Could not save the property logo')
                },
            },
        )
    }

    const handleRemove = () => {
        deleteLogo(
            { apartmentId },
            {
                onSuccess: () => {
                    setOverrideUrl('')
                    onSaved('')
                    toast.success('Property logo removed')
                },
                onError: () => {
                    toast.error('Could not remove the property logo')
                },
            },
        )
    }

    return (
        <Box className={classes.logoRow}>
            {busy ? (
                <CircularProgress size={24} />
            ) : shown ? (
                <img
                    src={shown}
                    alt="Property logo"
                    className={classes.logoPreview}
                />
            ) : (
                <Typography className={classes.caption}>
                    No logo saved yet.
                </Typography>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                style={{ display: 'none' }}
                onChange={handleFile}
            />
            <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
            >
                {shown ? 'Change logo' : 'Upload logo'}
            </Button>
            {shown ? (
                <Button size="small" onClick={handleRemove} disabled={busy}>
                    Remove
                </Button>
            ) : null}
            <Typography className={classes.caption}>
                Property logo. Fill from website can also use the header mark.
            </Typography>
        </Box>
    )
}

function replaceAt<T>(items: T[], index: number, value: T): T[] {
    return items.map((item, i) => (i === index ? value : item))
}

function triStateValue(value: boolean | null): string {
    if (value === true) return 'yes'
    if (value === false) return 'no'
    return 'unknown'
}

function parseTriState(value: string): boolean | null {
    if (value === 'yes') return true
    if (value === 'no') return false
    return null
}
